import { OptionType } from './types';
import { CalendarDate } from '@internationalized/date';
import React from 'react';
import { InteractionsType } from './globalContant';
import { ErrorStateType } from './Component/Profile/Providers/instituteProfileProvider';

export const getClassOptions = (classInfo: { class: string }[]) => {
  return classInfo.map((item, index) => ({
    id: index + 1,
    name: item.class,
  }));
};
export const getSelectedKey = (options: OptionType[], selectedValue?: string) => options.find((item) => item.name === selectedValue)?.id || -1;

export const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// eslint-disable-next-line
export const hasEmptyStringInNested = (obj: any): boolean => {
  if (typeof obj !== 'object' || obj === null) return false;

  return Object.values(obj).some((value) => value === '' || (typeof value === 'object' && hasEmptyStringInNested(value)));
};

// eslint-disable-next-line
export const hasEmptyStringInArray = (arr: any) => {
  if (!Array.isArray(arr)) return false;
  return arr.some((obj) => hasEmptyStringInNested(obj));
};

export const generateSections = (data: {
  class: string;
  sectionCount: number;
  sectionSystem: string;
  educationBoard: string;
}): { id: number; name: string }[] => {
  const { sectionCount, sectionSystem } = data;
  const sections: { id: number; name: string }[] = [];

  const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  const greekLetters = ['Α', 'Β', 'Γ', 'Δ', 'Ε', 'Ζ', 'Η', 'Θ', 'Ι', 'Κ'];
  const colors = ['Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple', 'Pink', 'Brown', 'Gray', 'Black'];

  for (let i = 1; i <= sectionCount; i++) {
    let sectionName = '';

    switch (sectionSystem) {
      case 'Roman':
        sectionName = romanNumerals[i - 1] || i.toString(); // Fallback to numbers if out of range
        break;
      case 'Alphabetic':
        sectionName = String.fromCharCode(64 + i); // A, B, C...
        break;
      case 'Numeric':
        sectionName = i.toString(); // 1, 2, 3...
        break;
      case 'Greek':
        sectionName = greekLetters[i - 1] || i.toString(); // Fallback if out of range
        break;
      case 'Ordinal':
        sectionName = `${i}${getOrdinalSuffix(i)}`; // 1st, 2nd, 3rd...
        break;
      case 'Color-Coded':
        sectionName = colors[i - 1] || `Color-${i}`; // Fallback to numbered colors if out of range
        break;
      default:
        sectionName = i.toString(); // Default to numeric if unknown type
        break;
    }

    sections.push({ id: i, name: sectionName });
  }

  return sections;
};

// eslint-disable-next-line
export const transformClassesData = (data: Array<{ class: string; section: string; subjects: any }>) => {
  const classMap = new Map();
  let classId = 0;

  data.forEach(({ class: className, section, subjects }) => {
    if (!classMap.has(className)) {
      classMap.set(className, {
        id: classId++,
        name: className,
        sections: [],
        subjects: {},
      });
    }

    const classEntry = classMap.get(className);
    if (!classEntry.sections.find((sec: { name: string }) => sec.name === section)) {
      classEntry.sections.push({
        id: classEntry.sections.length,
        name: section,
      });
    }
    classEntry.subjects[section] = subjects;
  });

  return Array.from(classMap.values());
};

const getOrdinalSuffix = (num: number): string => {
  if (num >= 11 && num <= 13) return 'th';
  switch (num % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const dateMinValue = new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());

const extractCorrectUrl = (duplicatedUrl: string) => {
  const match = duplicatedUrl.match(/https:\/\/storage\.googleapis\.com\/educotech\.appspot\.com\/(https:.+)/);

  if (match && match[1]) {
    return match[1].replace(/^https:\//, 'https://');
  }
  return duplicatedUrl;
};

export const fixGCSUrl = (url: string): string => {
  const toRemove = 'https%3A/storage.googleapis.com/educotech.appspot.com/';
  if (url.includes(toRemove)) {
    return url.replace(toRemove, '');
  }
  return extractCorrectUrl(url);
};

export const generateRandomString = (length = 20) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }
  return result;
};

export const capitalizeWords = (str: string) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getEmployeeName = (employeeList: { uid: string; name: string; id: string }[], uid: string) => {
  const employee = employeeList.find((item) => item.uid === uid || item.id === uid);
  return employee ? employee.name : 'NA';
};

export const getEmployeeId = (employeeList: { uid: string; name: string; id: string }[], uid: string) => {
  const employee = employeeList.find((item) => item.uid === uid);
  return employee ? employee.id : 'NA';
};

export const getIamgeFromFile = (file: File): string => {
  return URL.createObjectURL(file);
};

// eslint-disable-next-line
export const noop = () => {};
// eslint-disable-next-line
export const asyncNoop = async () => {};
// eslint-disable-next-line
const CustomNextArrow: React.FC<any> = ({ className, style, onClick }) => {
  return (
    <div
      className={className}
      style={{
        ...style,
        display: 'block',
        backgroundColor: '#42A5F5',
        color: 'white',
        borderRadius: '50%',
        padding: '1px',
        zIndex: 2,
        right: '10px',
      }}
      onClick={onClick}
    />
  );
};
// eslint-disable-next-line
const CustomPrevArrow: React.FC<any> = ({ className, style, onClick }) => {
  return (
    <div
      className={className}
      style={{
        ...style,
        display: 'block',
        backgroundColor: '#42A5F5',
        color: 'white',
        borderRadius: '50%',
        padding: '1px',
        zIndex: 2,
        left: '10px',
      }}
      onClick={onClick}
    />
  );
};

export const settings = {
  dots: true,
  infinite: true,
  speed: 3000,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 2000,
  nextArrow: <CustomNextArrow />,
  prevArrow: <CustomPrevArrow />,
};

export const getLikeCount = (interactions: InteractionsType[]) => {
  let likeCount = 0;
  if (typeof interactions === 'string') {
    JSON.parse(interactions)?.forEach((comment: InteractionsType) => {
      if (comment.likeResponse === true) {
        likeCount++;
      }
    });
  } else {
    interactions?.forEach((comment: InteractionsType) => {
      if (comment.likeResponse === true) {
        likeCount++;
      }
    });
  }
  return likeCount;
};

export const getDislikeCount = (interactions: InteractionsType[]) => {
  let dislikeCount = 0;
  if (typeof interactions === 'string') {
    JSON.parse(interactions)?.forEach((comment: InteractionsType) => {
      if (comment.likeResponse === false) {
        dislikeCount++;
      }
    });
  } else {
    interactions?.forEach((comment: InteractionsType) => {
      if (comment.likeResponse === false) {
        dislikeCount++;
      }
    });
  }
  return dislikeCount;
};

export const validateEmail = (email: string, setErrorState?: React.Dispatch<React.SetStateAction<ErrorStateType[]>>, fieldName?: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const isValid = emailRegex.test(email);
  if (!isValid && setErrorState) {
    setErrorState((prev) => [
      ...prev,
      {
        field: fieldName ?? '',
        message: 'Please enter a valid email',
      },
    ]);
  } else {
    if (setErrorState)
      setErrorState((prev) => {
        return prev.filter((item) => item.field !== fieldName);
      });
  }
  return isValid;
};

export const getCurrentDay = () => {
  const today = new Date();
  return today.toLocaleDateString('en-US', { weekday: 'long' });
};
export const getCurrentMonth = () => {
  const today = new Date();
  return today.toLocaleDateString('en-US', { month: 'long' });
};

export function isPastMonthYear(month: string, year: number | string): boolean {
  const monthIndex = new Date(`${month} 1, ${year}`).getMonth(); // Converts to 0-based index
  const inputDate = new Date(Number(year), monthIndex);

  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth());

  return inputDate < currentMonthStart;
}
